import { BannerConfigType, CouponVisibilityExtentType } from './customizeUI'
import { CouponVisibilityStateConfigType, CustomisablePageType, ThemeColorPaletteType } from './merchantAttributes'

import { Customizations, Milestone, MilestoneCategory, ProgressBarType } from '@shopflo/ui'

import { CartNoteErrorType } from '../types/cart'
import { BorderSides, CART_SCROLLABLE_ELEMENTS_TYPE, DISCOUNT_DISPLAY_TYPE } from 'src/components/cartV2/config'

export type CartContextState = {
  isEditingMilestone: boolean
  draftMilestone: Milestone
  countBasedProgressBar: ProgressBarType
  amountBasedProgressBar: ProgressBarType
  progressBar: ProgressBarType
  initialProgressBar: ProgressBarType
  cartUIData: customizeCartUIType
  initialCartUIData: customizeCartUIType
  cartNotes: CartNoteType
  initialCartNotes: CartNoteType
  cartNoteErrors: CartNoteErrorType
  showBannerErrors: boolean
}

export type CartContextActions = {
  setCartContext: (context: CartContextState) => void
  setIsProgressBarEnabled: (isEnabled: boolean) => void
  setIsEditingMilestone: (isEditing: boolean) => void
  setDraftMilestone: (milestone: Milestone) => void
  setMilestones: (milestones: Milestone[]) => void
  addMilestone: (milestone: Milestone) => void
  setCustomizations: (customizations: Customizations) => void
  saveProgressbar: () => Promise<void>
  discardChanges: () => void
  setActiveProgressBar: (category: MilestoneCategory) => void
  setCartBannerConfig: (payload: BannerConfigType) => void
  setCartButtonCTAText: (payload: string) => void
  discardCartUIChanges: () => void
  saveCartUIConfigData: () => Promise<void>
  setCartWidthForMobile: (payload: CART_WIDTH_FOR_MOBILE) => void
  setCartBottomBannerConfig: (payload: BannerConfigType) => void
  setCartNotes: (payload: CartNoteType) => void
  setInitialCartNotes: (payload: CartNoteType) => void
  setCartNoteErrors: (payload: CartNoteErrorType) => void
  setCartCouponVisibilityConfig: (payload: CouponVisibilityStateConfigType[]) => void
  setShowUpiIconsOnCartButton: (payload: boolean) => void
  setCartMarketingFlag: (payload: boolean) => void
  setCartUIConfigData: (payload: Record<string, unknown>, type: CART_UPDATE_TYPE) => void
}

export type CartContextGetters = {
  canCreateMilestone: boolean
  customizations: Customizations
  milestones: Milestone[]
  rawMilestones: Milestone[]
  hasUnsavedCartWidgetChanges: boolean
  hasUnsavedCartNoteChanges: boolean
  canProgressBeEnabled: boolean
  isAmountBased: boolean
  isCountBased: boolean
  colorPaletteData: ThemeColorPaletteType
  cartBannerUIConfigData: BannerConfigType
  hasUnsavedCartUIChanges: boolean
  cartWidthForMobile: CART_WIDTH_FOR_MOBILE
  cartBottomBannerUIConfigData: BannerConfigType
  cartNotes: CartNoteType
  initialCartNotes: CartNoteType
  cartNoteErrors: CartNoteErrorType
  cartUIData: customizeCartUIType
  showBannerErrors: boolean
}

export enum ActionType {
  SET_CART_CONTEXT,
  SET_IS_PROGRESS_BAR_ENABLED,
  ADD_MILESTONE,

  SET_AMOUNT_BASED_PROGRESS_BAR_AS_ACTIVE,
  SET_COUNT_BASED_PROGRESS_BAR_AS_ACTIVE,
  SET_IS_EDITING_MILESTONE,
  SET_DRAFT_MILESTONE,
  SET_MILESTONES,
  SET_CUSTOMIZATIONS,
  SET_INITAL_PROGRESS_BAR_STATE,

  SAVE_PROGRESSBAR,
  DISCARD_CHANGES,
  SET_CART_BANNER_CONFIG_DATA,
  SET_CART_BUTTON_CTA_TEXT,
  SET_CART_COUPON_VISIBILITY_CONFIG,
  SET_INITIAL_CUSTOMIZE_UI_CART_DATA,
  DISCARD_CART_UI_CHANGES,
  SAVE_CART_UI_CONFIG_DATA,
  SET_CART_WIDTH,
  SET_CART_BOTTOM_BANNER_CONFIG_DATA,
  SET_CART_NOTES,
  SET_INITIAL_CART_NOTES,
  SET_CART_NOTE_ERROR,
  SET_SHOW_UPI_ICONS_ON_CART_BUTTON,
  SET_CART_MARKETING_FLAG,
  SET_CART_LAYOUT,
  SET_CART_HEADER,
  SET_CART_FOOTER,
  SET_CART_PRIMARY_CTA_CONFIG,
  SET_CART_ADDON_CONFIG,
  SET_CART_ELEMENT_ORDER,
  SET_CART_ORDER_SUMMARY_CONFIG,
  SET_CART_DISCOUNT_CONFIG,
  SET_CART_UPSELL_CONFIG,
  SET_CART_NOTES_CONFIG,
  SET_CART_COUPON_CARD_CONFIG,
  SET_SHOW_BANNER_ERRORS
}

export type Action =
  | { type: ActionType.SET_AMOUNT_BASED_PROGRESS_BAR_AS_ACTIVE }
  | { type: ActionType.SET_COUNT_BASED_PROGRESS_BAR_AS_ACTIVE }
  | { type: ActionType.SET_CART_CONTEXT; payload: CartContextState }
  | { type: ActionType.SET_IS_PROGRESS_BAR_ENABLED; payload: boolean }
  | { type: ActionType.SET_IS_EDITING_MILESTONE; payload: boolean }
  | { type: ActionType.SET_DRAFT_MILESTONE; payload: Milestone }
  | { type: ActionType.SET_MILESTONES; payload: Milestone[] }
  | { type: ActionType.SET_CUSTOMIZATIONS; payload: Customizations }
  | { type: ActionType.SET_INITAL_PROGRESS_BAR_STATE; payload: CartContextState['progressBar'] }
  | { type: ActionType.ADD_MILESTONE; payload: Milestone }
  | { type: ActionType.SAVE_PROGRESSBAR }
  | { type: ActionType.DISCARD_CHANGES }
  | { type: ActionType.SET_CART_BANNER_CONFIG_DATA; payload: BannerConfigType }
  | { type: ActionType.SET_CART_BOTTOM_BANNER_CONFIG_DATA; payload: BannerConfigType }
  | { type: ActionType.SET_CART_BUTTON_CTA_TEXT; payload: string }
  | { type: ActionType.DISCARD_CART_UI_CHANGES }
  | { type: ActionType.SET_INITIAL_CUSTOMIZE_UI_CART_DATA; payload: customizeCartUIType }
  | { type: ActionType.SAVE_CART_UI_CONFIG_DATA }
  | { type: ActionType.SET_CART_WIDTH; payload: CART_WIDTH_FOR_MOBILE }
  | { type: ActionType.SET_CART_NOTES; payload: CartNoteType }
  | { type: ActionType.SET_INITIAL_CART_NOTES; payload: CartNoteType }
  | { type: ActionType.SET_CART_NOTE_ERROR; payload: CartNoteErrorType }
  | { type: ActionType.SET_CART_COUPON_VISIBILITY_CONFIG; payload: CouponVisibilityStateConfigType[] }
  | { type: ActionType.SET_SHOW_UPI_ICONS_ON_CART_BUTTON; payload: boolean }
  | { type: ActionType.SET_CART_MARKETING_FLAG; payload: boolean }
  | { type: ActionType.SET_CART_LAYOUT; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_HEADER; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_FOOTER; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_PRIMARY_CTA_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_ADDON_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_ELEMENT_ORDER; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_ORDER_SUMMARY_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_DISCOUNT_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_UPSELL_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_NOTES_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_CART_COUPON_CARD_CONFIG; payload: Record<string, unknown> }
  | { type: ActionType.SET_SHOW_BANNER_ERRORS; payload: boolean }

export type CART_UPDATE_TYPE =
  | 'LAYOUT'
  | 'HEADER'
  | 'FOOTER'
  | 'PRIMARY_CTA_CONFIG'
  | 'ADDON_CONFIG'
  | 'ELEMENT_ORDER'
  | 'ORDER_SUMMARY'
  | 'DISCOUNT'
  | 'UPSELL'
  | 'NOTES'
  | 'COUPON_CARD'

export type FooterConfigType = {
  backgroundColor: string
  textColor: string
  savingsColor: string
  showSavings: boolean
}

export type HeaderConfigType = {
  backgroundColor: string
  textColor: string
}

export type pageConfigType = {
  backgroundColor: string
}

export type customizeCartUIType = {
  showUpiIconsOnCartButton: boolean
  enableMarketingFlag: boolean
  cartBannerUIConfigData: BannerConfigType
  cartBottomBannerUIConfigData: BannerConfigType
  colorPaletteData: ThemeColorPaletteType
  cartWidthForMobile: CART_WIDTH_FOR_MOBILE
  couponVisibilityConfig: CouponVisibilityStateConfigType[]
  page: {
    backgroundColor: string
  }
  header: {
    backgroundColor: string
    textColor: string
  }
  footer: {
    backgroundColor: string
    textColor: string
    savingsColor: string
    showSavings: boolean
  }
  primaryCTAConfig: {
    backgroundColor?: string
    textColor: string
    borderRadius: number
    cartButtonCTAText: string
  }
  addonConfig: {
    backgroundColor: string
    textColor: string
    buttonColor?: string
  }
  orderSummaryConfig: {
    backgroundColor: string
    borderColor: string
    offerBadgeColor: string
    textColor: string
    quantityEditorBackground: string
    quantityEditorText: string
    cardRadius: number
    cardBorderWidth: number
    cardBorderSides: BorderSides
  }
  upsell: {
    isEnabled: boolean
    title: string
    discountedTitle: string
    shouldOverrideDiscountedUpsell: boolean
    backgroundColor: string
    borderColor: string
    textColor: string
    buttonColor: string
    offerBadgeColor: string
    titleColor: string
    cardRadius: number
    cardBorderWidth: number
    cardBorderSides: BorderSides
  }
  notesConfig: {
    isEnabled: boolean
    backgroundColor: string
    borderColor: string
    textColor: string
    buttonColor: string
    cardRadius: number
    cardBorderWidth: number
    cardBorderSides: BorderSides
  }
  discountConfig: {
    callout: {
      backgroundColor: string
      infoColor: string
      primaryColor: string
      borderColor: string
    }
    footer: {
      backgroundColor: string
      infoColor: string
      primaryColor: string
    }
    input: {
      backgroundColor: string
      borderColor: string
      primaryColor: string
    }
    button: {
      backgroundColor: string
      textColor: string
    }
    totalSavings: {
      primaryColor: string
      infoColor: string
      backgroundColor: string
    }
    cardRadius: number
    cardBorderWidth: number
    cardBorderSides: BorderSides
    buttonRadius?: number
    filterRadius?: number
  }
  elementOrder: CART_SCROLLABLE_ELEMENTS_TYPE[]
  couponPageConfig: {
    backgroundColor: string
    textColor: string
    primaryColor: string
    cardRadius: number
    filterRadius: number
    buttonRadius: number
  }
}

export type CartUIConfigTypeForBackend = {
  page: {
    background_color: string
  }
  layout: {
    enable_marketing_flag: boolean
  }
  header: {
    background_color: string
    text_color: string
  }
  footer: {
    background_color: string
    text_color: string
    savings_color: string
    show_savings: boolean
  }
  primary_cta_config: PrimaryCTATextTypeForBackend[]
  banner_settings: BannerSettingsTypeForBackend
  banner_config: BannerConfigTypeForBackend[]
  bottom_banner_config: BannerConfigTypeForBackend[]
  cart_overlay_width: CART_WIDTH_FOR_MOBILE
  coupon_visibility_config: CouponVisibilityConfigTypeForBackend[]
  show_upi_icons_on_cart_button: boolean
  order_summary_config: OrderSummaryConfigTypeForBackend
  discount_config: DiscountConfigTypeForBackend
  upsell: UpsellConfigTypeForBackend
  notes_config: NotesConfigTypeForBackend
  addon_config: AddOnConfigTypeForBackend
  coupon_page_config: CouponPageConfigTypeForBackend
  element_order: CART_SCROLLABLE_ELEMENTS_TYPE[]
}

export type CartButtonCTAText = {
  type: CustomisablePageType
  CTAText: string
}

export type BannerSettingsTypeForBackend = {
  enabled: boolean
  multiple_banners_enabled: boolean
  animation_style?: string
  animation_delay?: number
}

export type BannerItemForBackend = {
  background_color: string
  text_color: string
  html_text_string: string
}

export type BannerConfigTypeForBackend = {
  enabled: boolean
  type: CustomisablePageType
  position: number
  background_color: string
  text_color: string
  html_text_string: string
}

export type PrimaryCTATextTypeForBackend = {
  type: CustomisablePageType
  cta_text: string
  background_color?: string
  text_color: string
  border_radius: number
}

export type OrderSummaryConfigTypeForBackend = {
  background_color: string
  border_color: string
  offer_badge_color: string
  text_color: string
  quantity_editor_background: string
  quantity_editor_text: string
  card_radius: number
  card_border_width: number
  card_border_sides: BorderSides
}

export type DiscountConfigTypeForBackend = {
  callout: {
    background_color: string
    info_color: string
    primary_color: string
    border_color: string
  }
  footer: {
    background_color: string
    info_color: string
    primary_color: string
  }
  input: {
    background_color: string
    border_color: string
    primary_color: string
  }
  button: {
    background_color: string
    text_color: string
  }
  total_savings: {
    primary_color: string
    info_color: string
    background_color: string
  }
  card_radius: number
  card_border_width: number
  card_border_sides: BorderSides
}

export type NotesConfigTypeForBackend = {
  is_enabled: boolean
  background_color: string
  border_color: string
  text_color: string
  button_color: string
  card_radius: number
  card_border_width: number
  card_border_sides: BorderSides
}

export type AddOnConfigTypeForBackend = {
  background_color: string
  text_color: string
  button_color?: string
}

export type CouponPageConfigTypeForBackend = {
  background_color: string
  text_color: string
  primary_color: string
  card_radius: number
  filter_radius: number
  button_radius: number
}

export type UpsellConfigTypeForBackend = {
  background_color: string
  border_color: string
  text_color: string
  button_color: string
  offer_badge_color: string
  title_color: string
  card_radius: number
  card_border_width: number
  card_border_sides: BorderSides
  is_enabled: boolean
  title: string
  discounted_title: string
  should_override_discounted_upsell: boolean
}

export enum CART_WIDTH_FOR_MOBILE {
  FULL = 'full',
  WINDOWED = 'windowed'
}

export type CartNoteType = {
  enabled: boolean
  icon: CartIcon
  title: string
  suggestionText: string
}

export type CouponVisibilityConfigTypeForBackend = {
  type: CustomisablePageType
  visibile: boolean
  visibility_extent: CouponVisibilityExtentType
  hide_savings: boolean
}

export type CartIcon = 'ATTACH' | 'EDIT' | 'GIFT'
